import { Group, InputDecimal } from '@/components/_common';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import ManufacturerColumnFilter from '@/components/_common/Manufacturer/ManufacturerColumnFilter';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import StatusTag from '@/components/_common/StatusTag';
import SubAreasColumnFilter from '@/components/_common/SubAreasColumnFilter';
import SubGroupColumnFilter from '@/components/_common/SubGroup/SubGroupColumnFilter';
import SubGroupsSelectField from '@/components/_common/SubGroupsSelectField';
import {
  WorkOrderImportGeneralFormValues,
  WorkorderImportItem,
} from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import {
  WorkOrderItemType,
  workorderTypeColor,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import {
  Button,
  Checkbox,
  Form,
  message,
  Modal,
  ModalProps,
  Popover,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { FaExchangeAlt, FaQuestionCircle } from 'react-icons/fa';

interface Props extends ModalProps {
  onOk: () => void;
  onCancel: () => void;
}

const WorkorderImportAreaItemsModal: React.FC<Props> = ({ ...props }) => {
  const form = Form.useFormInstance<WorkOrderImportGeneralFormValues>();
  const formItems = Form.useWatch('items', form);
  const [items, setItems] = useState<WorkorderImportItem[]>([]);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filterGroupOpen, setFilterGroupOpen] = useState(false);
  const [filterManufactureOpen, setFilterManufactureOpen] = useState(false);

  useEffect(() => {
    if (formItems?.length && props.open) {
      setItems(formItems);
    }
  }, [formItems, props.open]);

  function handleConfirm() {
    try {
      if (!items.every((item) => !!item.subAreaId)) {
        return message.warning(
          'Preencha todas as áreas dos items da importação!',
        );
      }

      if (
        !items
          .filter(
            (item) => !item.itemId && item.type === WorkOrderItemType.Part,
          )
          .every((item) => !!item.subGroupId && !!item.manufacturerId)
      ) {
        return message.warning(
          'Preencha todos os grupos e os fabricantes dos items sem registros da importação!',
        );
      }

      form.setFieldValue('items', items);

      props.onOk();
    } catch {
      return message.error('Houve um erro, tente novamente!');
    }
  }

  function handleClose() {
    props.onCancel();
    setItems([]);
  }

  function handleChange(
    field: keyof WorkorderImportItem,
    index: number,
    value?: string | number,
  ) {
    setItems((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  }

  function setSubAreaId(subAreaId: number) {
    const newItems = items.map((item) => ({
      ...item,
      subAreaId,
    }));
    setItems(newItems);
    setFilterDropdownOpen(false);
  }

  function setSubGroupId(subGroupId: number) {
    const newItems = items.map((item) =>
      !item.itemId
        ? {
            ...item,
            subGroupId:
              item.type === WorkOrderItemType.Part ? subGroupId : undefined,
          }
        : item,
    );
    setItems(newItems);
    setFilterGroupOpen(false);
  }

  function setManufacturerId(manufacturerId: number) {
    const newItems = items.map((item) =>
      !item.itemId
        ? {
            ...item,
            manufacturerId:
              item.type === WorkOrderItemType.Part ? manufacturerId : undefined,
          }
        : item,
    );
    setItems(newItems);
    setFilterManufactureOpen(false);
  }

  const columns: ColumnsType<WorkorderImportItem> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
      width: 250,
      ellipsis: true,
    },
    {
      dataIndex: 'type',
      title: 'Tipo',
      width: 80,
      ellipsis: true,
      render: (_, item) =>
        item.type === WorkOrderItemType.Part ||
        item.type === WorkOrderItemType.Service ? (
          <StatusTag
            color={workorderTypeColor[item.type]}
            label={item.type === WorkOrderItemType.Service ? 'Serviço' : 'Peça'}
          />
        ) : (
          '-'
        ),
    },
    {
      dataIndex: 'subAreaId',
      title: 'Área',
      width: 200,
      filterDropdownOpen,
      onFilterDropdownOpenChange: setFilterDropdownOpen,
      filterDropdown: () => {
        return <SubAreasColumnFilter onChange={setSubAreaId} />;
      },
      filterIcon: <FaExchangeAlt size={14} />,
      render: (rowValue, _, index) => (
        <AreasInfiniteSelectField
          value={rowValue}
          onChange={(value) => handleChange('subAreaId', index, value)}
          queryFilter={{ active: true }}
        />
      ),
    },
    {
      dataIndex: 'subGroupId',
      title: 'Grupo',
      width: 200,
      filterDropdownOpen: filterGroupOpen,
      onFilterDropdownOpenChange: setFilterGroupOpen,
      filterDropdown: () => {
        return <SubGroupColumnFilter onChange={setSubGroupId} />;
      },
      filterIcon: <FaExchangeAlt size={14} />,
      render: (rowValue, row, index) => (
        <SubGroupsSelectField
          value={rowValue}
          style={{ width: 200 }}
          disabled={!!row.itemId || row.type == WorkOrderItemType.Service}
          onChange={(value) => handleChange('subGroupId', index, value)}
        />
      ),
    },
    {
      dataIndex: 'manufacturerId',
      title: 'Fabricante',
      width: 200,
      filterDropdownOpen: filterManufactureOpen,
      onFilterDropdownOpenChange: setFilterManufactureOpen,
      filterDropdown: () => {
        return <ManufacturerColumnFilter onChange={setManufacturerId} />;
      },
      filterIcon: <FaExchangeAlt size={14} />,
      render: (rowValue, row, index) => (
        <ManufacturerInfiniteSelectField
          value={rowValue}
          disabled={!!row.itemId || row.type == WorkOrderItemType.Service}
          style={{ width: 200 }}
          onChange={(value) => handleChange('manufacturerId', index, value)}
        />
      ),
    },
    {
      dataIndex: 'unitPrice',
      title: 'Valor unitário',
      width: 150,
      render: (rowValue, _, index) => (
        <InputDecimal
          style={{ width: 200 }}
          value={rowValue}
          onChange={(value) => handleChange('unitPrice', index, value)}
        />
      ),
    },
  ];

  return (
    <Modal
      {...props}
      title="Selecionar área de cada item"
      okText="Confirmar importação"
      onOk={handleConfirm}
      onCancel={handleClose}
      width="90%"
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 16 }}
      footer={
        <Group position="between">
          <Group>
            <Form.Item
              name="freeUnitCostService"
              initialValue={false}
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Serviços internos sem custo</Checkbox>
            </Form.Item>

            <Popover
              title="Importar como serviço cadastrado"
              content={
                <div style={{ maxWidth: 400 }}>
                  Ao marcar esta opção, os serviços importados da OS serão
                  tratados como serviços cadastrados no sistema, ou seja,{' '}
                  <strong>sem custo</strong>. Essa configuração evita que o
                  sistema crie serviços internos com custo, preservando a margem
                  de lucro.
                </div>
              }
            >
              <FaQuestionCircle size={12} />
            </Popover>
          </Group>

          <Group>
            <Button onClick={handleClose}>Cancelar</Button>

            <Button type="primary" onClick={handleConfirm} loading={props.confirmLoading}>
              Confirmar importação
            </Button>
          </Group>
        </Group>
      }
    >
      <p>
        É obrigatorio a seleção de área de cada item da importação, e caso os
        itens não estejam cadastrados é necessário informar o grupo e o
        fabricante dos itens.
      </p>

      <Typography.Title level={4}>Items de importação</Typography.Title>

      <Table
        dataSource={items}
        pagination={false}
        rowKey={(_, index) => String(index)}
        scroll={{ x: true }}
        columns={columns}
      />
    </Modal>
  );
};

export default WorkorderImportAreaItemsModal;
